import * as React from "react";
import { cn } from "./ui/index.ts";
import { I } from "./icons/icons.tsx";
import { navArrowBtn, navCenterBtn } from "./page-layout.ts";

export interface OptionStepItem {
  value: string;
  label: string;
  badge?: string | number;
}

/** Précédent · action centrale · suivant (hauteur 32px uniforme). */
export function DateStepper({
  onPrev,
  onNext,
  onToday,
  todayActive,
  todayLabel = "Aujourd'hui",
  className,
}: {
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  todayActive: boolean;
  todayLabel?: string;
  className?: string;
}) {
  return (
    <div className={cn("flex shrink-0 items-center gap-1", className)}>
      <button
        type="button"
        onClick={onPrev}
        className={navArrowBtn()}
        aria-label="Précédent"
      >
        <I.ChevronLeft size={13} />
      </button>
      <button
        type="button"
        onClick={onToday}
        className={navCenterBtn(todayActive)}
      >
        {todayLabel}
      </button>
      <button
        type="button"
        onClick={onNext}
        className={navArrowBtn()}
        aria-label="Suivant"
      >
        <I.ChevronRight size={13} />
      </button>
    </div>
  );
}

/** Parcourir les options d’un filtre / menu par flèches (remplace les onglets pilule). */
export function OptionStepper({
  options,
  value,
  onChange,
  className,
  ariaLabel = "Sélection",
}: {
  options: OptionStepItem[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
  ariaLabel?: string;
}) {
  const enabled = options.length > 0 ? options : [{ value: "", label: "—" }];
  const idx = Math.max(
    0,
    enabled.findIndex((o) => o.value === value),
  );
  const current = enabled[idx] ?? enabled[0]!;
  const cycle = (delta: number) => {
    const next = enabled[(idx + delta + enabled.length) % enabled.length]!;
    onChange(next.value);
  };

  return (
    <div
      className={cn("flex shrink-0 items-center gap-1", className)}
      role="group"
      aria-label={ariaLabel}
    >
      <button
        type="button"
        onClick={() => cycle(-1)}
        className={navArrowBtn()}
        aria-label="Option précédente"
        disabled={enabled.length <= 1}
      >
        <I.ChevronLeft size={13} />
      </button>
      <span
        className={cn(navCenterBtn(), "gap-1.5 px-3")}
        title={current.label}
      >
        <span className="truncate">{current.label}</span>
        {current.badge != null && (
          <span className="shrink-0 rounded-md bg-sf-surface-2 px-1.5 py-px text-[10px] font-bold tabular-nums text-sf-text-muted">
            {current.badge}
          </span>
        )}
      </span>
      <button
        type="button"
        onClick={() => cycle(1)}
        className={navArrowBtn()}
        aria-label="Option suivante"
        disabled={enabled.length <= 1}
      >
        <I.ChevronRight size={13} />
      </button>
    </div>
  );
}
